import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { animated, useSprings } from 'react-spring';

// 直接导入所有公司logo
import company2066 from '../../assets/images/companys/Frame 2066.png';
import company2067 from '../../assets/images/companys/Frame 2067.png';
import company2068 from '../../assets/images/companys/Frame 2068.png';
import company2069 from '../../assets/images/companys/Frame 2069.png';
import company2070 from '../../assets/images/companys/Frame 2070.png';
import company2071 from '../../assets/images/companys/Frame 2071.png';
import company2072 from '../../assets/images/companys/Frame 2072.png';
import company2073 from '../../assets/images/companys/Frame 2073.png';
import company2076 from '../../assets/images/companys/Frame 2076.png';
import company2078 from '../../assets/images/companys/Frame 2078.png';
import company2079 from '../../assets/images/companys/Frame 2079.png';
import company2080 from '../../assets/images/companys/Frame 2080.png';
import company2081 from '../../assets/images/companys/Frame 2081.png';
import company2082 from '../../assets/images/companys/Frame 2082.png';
import company2083 from '../../assets/images/companys/Frame 2083.png';
import company2084 from '../../assets/images/companys/Frame 2084.png';
import company2085 from '../../assets/images/companys/Frame 2085.png';
import company2086 from '../../assets/images/companys/Frame 2086.png';

const Partners = () => {
  const [isHovered, setIsHovered] = useState(false);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);

  // 使用useMemo缓存客户数据 - 使用真实公司图片
  const companyImages = useMemo(() => [
    company2066,
    company2067,
    company2068,
    company2069,
    company2070,
    company2071,
    company2072,
    company2073,
    company2076,
    company2078,
    company2079,
    company2080,
    company2081,
    company2082,
    company2083,
    company2084,
    company2085,
    company2086,
  ], []);

  // 使用useMemo缓存客户数据
  const clients = useMemo(() => companyImages.map((image, i) => ({
    id: i + 1,
    image,
    color: [
      'from-amber-500 to-yellow-500',
      'from-purple-500 to-violet-500',
      'from-emerald-500 to-green-500',
      'from-cyan-500 to-teal-500',
      'from-pink-500 to-rose-500',
      'from-indigo-500 to-blue-500',
      'from-orange-500 to-red-500',
      'from-lime-500 to-green-500',
    ][i % 8]
  })), [companyImages]);

  // 使用useMemo缓存所有客户数据
  const allClients = useMemo(() => [...clients, ...clients, ...clients], [clients]);

  // 使用useCallback缓存动画配置
  const springConfig = useMemo(() => ({
    mass: 1,
    tension: 170,
    friction: 26,
    velocity: 0
  }), []);

  // 使用react-spring创建物理动画
  const [springs, api] = useSprings(allClients.length, (index) => ({
    x: index * -12,
    config: springConfig
  }));

  // 使用useCallback缓存动画函数
  const animate = useCallback((timestamp) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const progress = (timestamp - startTimeRef.current) % 30000;
    const currentPosition = (progress / 30000) * (allClients.length * -12);

    if (!isHovered) {
      api.start((index) => ({
        x: (index * -12 + currentPosition) % (allClients.length * -12),
        immediate: false
      }));
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [api, isHovered, allClients.length]);

  // 使用useCallback缓存鼠标事件处理函数
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // 滚动动画
  useEffect(() => {
    if (!isHovered) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animate, isHovered]);

  // 使用useMemo缓存背景元素
  const backgroundElements = useMemo(() => (
    <div className="absolute inset-0">
      <div className="absolute top-1/4 right-0 w-[800px] h-[800px] bg-[#F1F600]/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
      <div className="absolute top-1/3 left-0 w-[700px] h-[700px] bg-[#FF4D4D]/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-1/4 left-1/2 w-[600px] h-[600px] bg-[#2C3E50]/20 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
    </div>
  ), []);

  // 使用useMemo缓存标题部分
  const headerSection = useMemo(() => (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-5xl font-bold mb-6">
          <span className="relative inline-block group">
            <span className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">合作客户</span>
            <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-amber-400 to-yellow-400 transform scale-x-50 group-hover:scale-x-100 transition-transform duration-500"></div>
          </span>
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">携手共创未来，见证卓越成长</p>
      </div>
    </div>
  ), []);

  return (
    <section className="relative py-16 md:py-24 bg-gradient-to-br from-white via-[#F1F600]/5 to-white overflow-hidden">
      {backgroundElements}
      {headerSection}

      {/* 全屏滚动容器 */}
      <div className="relative container-wide">
        <div className="flex space-x-8 py-8">
          {springs.map((props, index) => {
            const client = allClients[index];
            return (
              <animated.div
                key={index}
                className="group relative flex-shrink-0 w-[200px] h-[124px]"
                style={props}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${client.color} rounded-[22px] blur opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="relative h-full w-full bg-white rounded-[20px] p-4 shadow-sm hover:shadow-lg transition-all duration-300">
                  <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                    <img 
                      src={client.image} 
                      alt={`合作伙伴 ${client.id}`} 
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                </div>
              </animated.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default React.memo(Partners); 